import { supabase } from '@/integrations/supabase/client';

interface RetryOptions {
  maxRetries: number;
  delayMs: number;
}

interface CreditPersistenceResult {
  success: boolean;
  newBalance?: number;
  error?: string;
}

export class CreditPersistenceService {
  private static readonly DEFAULT_RETRY_OPTIONS: RetryOptions = {
    maxRetries: 3,
    delayMs: 1000,
  };

  /**
   * Get user credit balance with retry logic
   */
  static async getUserBalance(userId: string, options = this.DEFAULT_RETRY_OPTIONS): Promise<CreditPersistenceResult> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= options.maxRetries; attempt++) {
      try {
        const { data: creditData, error } = await supabase
          .from('user_credits')
          .select('balance')
          .eq('user_id', userId)
          .maybeSingle();

        if (error && error.code !== 'PGRST116') {
          throw new Error(error.message);
        }

        return {
          success: true,
          newBalance: creditData?.balance || 0,
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.warn(`Credit fetch attempt ${attempt} failed:`, lastError.message);
        
        if (attempt < options.maxRetries) {
          await this.delay(options.delayMs * attempt);
        }
      }
    }

    return {
      success: false,
      error: `Failed to fetch credits after ${options.maxRetries} attempts: ${lastError?.message}`,
    };
  }

  /**
   * Update user credit balance with retry logic
   */
  static async updateUserBalance(
    userId: string, 
    newBalance: number, 
    options = this.DEFAULT_RETRY_OPTIONS
  ): Promise<CreditPersistenceResult> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= options.maxRetries; attempt++) {
      try {
        // First check if user has existing record
        const { data: existingCredit, error: fetchError } = await supabase
          .from('user_credits')
          .select('balance')
          .eq('user_id', userId)
          .maybeSingle();

        if (fetchError && fetchError.code !== 'PGRST116') {
          throw new Error(fetchError.message);
        }

        if (existingCredit) {
          // Update existing balance
          const { error: updateError } = await supabase
            .from('user_credits')
            .update({ 
              balance: newBalance,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', userId);

          if (updateError) {
            throw new Error(updateError.message);
          }
        } else {
          // Insert new balance record
          const { error: insertError } = await supabase
            .from('user_credits')
            .insert({ 
              user_id: userId, 
              balance: newBalance,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) {
            throw new Error(insertError.message);
          }
        }

        return {
          success: true,
          newBalance: newBalance,
        };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.warn(`Credit update attempt ${attempt} failed:`, lastError.message);
        
        if (attempt < options.maxRetries) {
          await this.delay(options.delayMs * attempt);
        }
      }
    }

    return {
      success: false,
      error: `Failed to update credits after ${options.maxRetries} attempts: ${lastError?.message}`,
    };
  }

  /**
   * Record a credit transaction with retry logic
   */
  static async recordTransaction(
    userId: string,
    type: string,
    amount: number,
    description: string,
    metadata?: any,
    options = this.DEFAULT_RETRY_OPTIONS
  ): Promise<{ success: boolean; error?: string }> {
    let lastError: Error | null = null;
    
    for (let attempt = 1; attempt <= options.maxRetries; attempt++) {
      try {
        const { error } = await supabase
          .from('credit_transactions')
          .insert({
            user_id: userId,
            type: type,
            amount: amount,
            description: description,
            timestamp: new Date().toISOString(),
            metadata: metadata || {}
          });

        if (error) {
          throw new Error(error.message);
        }

        return { success: true };
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');
        console.warn(`Transaction recording attempt ${attempt} failed:`, lastError.message);
        
        if (attempt < options.maxRetries) {
          await this.delay(options.delayMs * attempt);
        }
      }
    }

    return {
      success: false,
      error: `Failed to record transaction after ${options.maxRetries} attempts: ${lastError?.message}`,
    };
  }

  /**
   * Utility method for delays
   */
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Verify credit balance consistency
   */
  static async verifyBalance(userId: string): Promise<{
    isConsistent: boolean;
    actualBalance: number;
    error?: string;
  }> {
    try {
      const result = await this.getUserBalance(userId);
      
      if (!result.success) {
        return {
          isConsistent: false,
          actualBalance: 0,
          error: result.error,
        };
      }

      return {
        isConsistent: true,
        actualBalance: result.newBalance || 0,
      };
    } catch (error) {
      return {
        isConsistent: false,
        actualBalance: 0,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }
} 